import { ChangeEvent, FC, useCallback, useLayoutEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import { Field, Form, Label } from '../../Registration.styles';
import { Dropdown, ImageUpload, Input } from '../../../../components';
import { Option } from '../../../../components/dropdown/Dropdown';
import { RegistrationFormPage } from '../../templates';
import { newGroupFormInitialState, newGroupFormReducer } from './newGroup.form';
import { getImageUrl, opcoesDeSimOuNao } from '../../../../utils';
import { useToastStore } from '../../../../stores';
import { getGroupDealerById, postGroupDealer, putAdminGroupLogo, putGroupDealer, TConcessionaria, TGrupoConcessionaria } from '../../../../services';
import { getIndicacaoUsuarios, IndicacaoUsuarioBusca } from '../../../../services/indicacao';
import { useNavigate, useParams } from 'react-router-dom';

const normalizarTagAtivo = (valor: unknown) => {
	if (valor === true || valor === 'true' || valor === 1 || valor === '1') return '1';
	if (valor === false || valor === 'false' || valor === 0 || valor === '0') return '0';
	if (valor === 2 || valor === '2') return '2';
	return String(valor ?? '').trim();
};

const NewGroup: FC = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const { setToast } = useToastStore();
	const [state, dispatch] = useReducer(newGroupFormReducer, newGroupFormInitialState);
	const [logotipoToUpload, setLogotipoToUpload] = useState<File | null>(null);
	const [logotipoOnServer, setLogotipoOnSever] = useState<string | undefined>(undefined);
	const [listIdDados, setListIdDados] = useState<TGrupoConcessionaria | undefined>(undefined);
	const [indicadorSelecionado, setIndicadorSelecionado] = useState<IndicacaoUsuarioBusca | null>(null);
	const [opcoesIndicador, setOpcoesIndicador] = useState<IndicacaoUsuarioBusca[]>([]);
	const [buscandoIndicador, setBuscandoIndicador] = useState(false);
	const buscaIndicadorRef = useRef(0);
	const termoIndicadorRef = useRef('');
	const debounceIndicadorRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		dispatch({ type: 'SET_VALUE', field: name, payload: value });
	};

	const handleUpdateText = (e: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		dispatch({ type: 'SET_VALUE', field: name, payload: value });
		setListIdDados((prevState) => ({
			...prevState,
			[name]: value,
		} as TGrupoConcessionaria));
	};

	const handleSelectOption = (field: string, option: Option) => {
		dispatch({ type: 'SET_SELECT_OPTION', field: field, value: option.value });
		setListIdDados((prevState) => ({
			...prevState,
			[field]: option.value,
		} as TGrupoConcessionaria));
	};

	const handleSubmitImages = async (groupId: string, logo: File) => {
		try {
			await putAdminGroupLogo(groupId, logo);
			return false;
		} catch {
			return true;
		}
	};

	const buscarIndicadores = useCallback(async (termo: string) => {
		const carga = ++buscaIndicadorRef.current;
		setBuscandoIndicador(true);
		try {
			const response = await getIndicacaoUsuarios(termo);
			if (carga !== buscaIndicadorRef.current) return;
			setOpcoesIndicador(response?.success ? response.data ?? [] : []);
		} catch {
			if (carga !== buscaIndicadorRef.current) return;
			setOpcoesIndicador([]);
		} finally {
			if (carga === buscaIndicadorRef.current) setBuscandoIndicador(false);
		}
	}, []);

	const agendarBuscaIndicador = (termo: string) => {
		if (debounceIndicadorRef.current) clearTimeout(debounceIndicadorRef.current);
		debounceIndicadorRef.current = setTimeout(() => {
			void buscarIndicadores(termo);
		}, 300);
	};

	const handleSubmit = async () => {
		const requestBody = {
			...state,
			tagAtivo: tagAtivoAtual === '1',
			logotipo: logotipoToUpload,
			idUserIndicador: indicadorSelecionado?.id,
			setToast,
			navigate,
		};

		let message = '';
		let groupId = id;

		if (groupId) {
			await putGroupDealer({ id: groupId, ...requestBody });
		} else {
			await postGroupDealer(requestBody);
		}

		if (groupId && logotipoToUpload) {
			const errorOnUpload = await handleSubmitImages(groupId, logotipoToUpload);
			if (errorOnUpload) message += ' Porém, ocorreu um erro no envio do logotipo.';
		}
	};

	useLayoutEffect(() => {
		const fetchGroupDealer = async () => {
			if (!id) return;

			try {
				const response = await getGroupDealerById({ id });

				if (!response) {
					throw new Error('Sem resposta da API');
				}

				const initialFormState = {
					grupoConcessionaria: response?.grupoConcessionaria,
					tagAtivo: normalizarTagAtivo(response?.tagAtivo),
					logotipo: response?.logotipo,
				};

				const grupoConcessionariaUrl = getImageUrl.grupoConcessionaria(response);
				const concessionariaUrl = getImageUrl.concessionaria({
					id: Number(id),
					logotipo: response?.logotipo,
				} as TConcessionaria);

				const testImage = (url: string) =>
					new Promise<boolean>((resolve) => {
						const img = new Image();
						img.src = url;
						img.onload = () => resolve(true);
						img.onerror = () => resolve(false);
					});

				const imageExists = await testImage(grupoConcessionariaUrl);
				const finalImageUrl = imageExists ? grupoConcessionariaUrl : concessionariaUrl;

				setListIdDados(response);
				dispatch({ type: 'REPLACE_STATE', payload: initialFormState });
				setLogotipoOnSever(finalImageUrl);
				if (response.idUserIndicador) {
					const indicador = {
						id: response.idUserIndicador,
						nome: response.nomeIndicador ?? '',
						email: response.emailIndicador ?? '',
					};
					setIndicadorSelecionado(indicador);
					setOpcoesIndicador([indicador]);
				} else {
					setIndicadorSelecionado(null);
				}
			} catch (err) {
				console.error('Erro ao buscar grupo concessionária:', err);
				setToast({ message: 'Erro ao buscar grupo concessionária', variant: 'error' });
			}
		};

		fetchGroupDealer();
	}, [id, setToast]);

	const statusSelecionado = useMemo(
		() => opcoesDeSimOuNao.find((status) => status.value === normalizarTagAtivo(state.tagAtivo || listIdDados?.tagAtivo)),
		[state.tagAtivo, listIdDados?.tagAtivo]
	);
	const descricaoPreenchida = Boolean(
		(id ? state.grupoConcessionaria || listIdDados?.grupoConcessionaria : state.grupoConcessionaria)
			?.toString()
			.trim()
	);
	const tagAtivoAtual = normalizarTagAtivo(id ? state.tagAtivo || listIdDados?.tagAtivo : state.tagAtivo);
	const desabledButton = !(descricaoPreenchida && tagAtivoAtual);

	const campoIndicador = (
		<CampoIndicador
			indicadorSelecionado={indicadorSelecionado}
			opcoesIndicador={opcoesIndicador}
			buscandoIndicador={buscandoIndicador}
			onOpen={() => void buscarIndicadores(termoIndicadorRef.current)}
			onInput={(value, reason) => {
				if (reason !== 'input' && reason !== 'clear') return;
				termoIndicadorRef.current = reason === 'clear' ? '' : value;
				setOpcoesIndicador([]);
				if (reason === 'clear') {
					void buscarIndicadores('');
					return;
				}
				agendarBuscaIndicador(value);
			}}
			onChange={setIndicadorSelecionado}
		/>
	);

	return (
		<>
			{!id ? (
				<RegistrationFormPage
					desabledButton={desabledButton}
					title="Cadastrar Grupo de Concessionárias"
					onSubmit={handleSubmit}
				>
					<Form>
						<Label>Descrição</Label>
						<Field>
							<Input
								variant="text"
								placeholder="Descrição"
								value={state.grupoConcessionaria}
								onChange={handleInputChange}
								name="grupoConcessionaria"
							/>
						</Field>
						<Label>Ativo?</Label>
						<Field>
							<Dropdown
								options={opcoesDeSimOuNao}
								selected={statusSelecionado}
								onSelect={(option: Option) => handleSelectOption('tagAtivo', option)}
							/>
						</Field>
						{campoIndicador}
						<Label>Logotipo</Label>
						<Field>
							<ImageUpload
								imageOnServer={logotipoOnServer}
								imageToUpload={logotipoToUpload}
								setImageToUpload={setLogotipoToUpload}
							/>
						</Field>
					</Form>
				</RegistrationFormPage>
			) : (
				<RegistrationFormPage
					desabledButton={desabledButton}
					title="Editar Grupo de Concessionárias"
					onSubmit={handleSubmit}
				>
					<Form>
						<Label>Descrição</Label>
						<Field>
							<Input
								variant="text"
								placeholder="Descrição"
								value={listIdDados?.grupoConcessionaria}
								onChange={handleUpdateText}
								name="grupoConcessionaria"
							/>
						</Field>
						<Label>Ativo?</Label>
						<Field>
							<Dropdown
								options={opcoesDeSimOuNao}
								selected={statusSelecionado}
								onSelect={(option: Option) => handleSelectOption('tagAtivo', option)}
								placeholder={listIdDados?.tagAtivo === '0' ? 'Não' : 'Sim'}
							/>
						</Field>
						{campoIndicador}
						<Label>Logotipo</Label>
						{listIdDados?.logotipo?.startsWith('https://homologacao.canaldorepasse.com.br') ? (
							<Field>
								<ImageUpload
									imageOnServer={logotipoOnServer}
									imageToUpload={logotipoToUpload}
									setImageToUpload={setLogotipoToUpload}
									altText="imagem para url"
								/>
							</Field>
						) : (
							<Field>
								<ImageUpload
									imageOnServer={logotipoOnServer}
									imageToUpload={logotipoToUpload}
									setImageToUpload={setLogotipoToUpload}
									altText="imagem para upload"
								/>
							</Field>
						)}
					</Form>
				</RegistrationFormPage>
			)}
		</>
	);
};

function CampoIndicador({
	indicadorSelecionado,
	opcoesIndicador,
	buscandoIndicador,
	onOpen,
	onInput,
	onChange,
}: {
	indicadorSelecionado: IndicacaoUsuarioBusca | null;
	opcoesIndicador: IndicacaoUsuarioBusca[];
	buscandoIndicador: boolean;
	onOpen: () => void;
	onInput: (value: string, reason: string) => void;
	onChange: (usuario: IndicacaoUsuarioBusca | null) => void;
}) {
	return (
		<>
			<Label>Indicador</Label>
			<Field>
				<Autocomplete
					size="small"
					fullWidth
					options={opcoesIndicador}
					value={indicadorSelecionado}
					loading={buscandoIndicador}
					filterOptions={(options) => options}
					getOptionLabel={(option) => option.nome}
					isOptionEqualToValue={(option, value) => option.id === value.id}
					noOptionsText="Nenhum usuário encontrado"
					loadingText="Buscando..."
					onOpen={onOpen}
					onInputChange={(_, value, reason) => onInput(value, reason)}
					onChange={(_, value) => onChange(value)}
					renderOption={(props, option) => {
						const { key, ...optionProps } = props;
						return (
							<li key={key} {...optionProps}>
								<Box minWidth={0}>
									<Typography fontSize="0.9rem" noWrap>
										{option.nome}
									</Typography>
									{option.email ? (
										<Typography fontSize="0.75rem" color="text.secondary" noWrap>
											{option.email}
										</Typography>
									) : null}
								</Box>
							</li>
						);
					}}
					renderInput={(params) => (
						<TextField {...params} placeholder="Opcional — busque pelo nome" />
					)}
				/>
			</Field>
		</>
	);
}

export default NewGroup;
